import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../ui';
import { cn } from '../../lib/utils';
import {
  FileSignature, Plus, Trash2, ChevronUp, ChevronDown, Printer,
  Play, Pause, SkipForward, SkipBack, Square, Clock, ClipboardCopy,
  FileText, Gavel, MessageSquare, BarChart3, Volume2, VolumeX,
  ChevronRight, Timer, Users, BookOpen, Landmark, Building2,
  LayoutTemplate, StickyNote, Check, X, Minus, AlertTriangle
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type MeetingMode = 'build' | 'present' | 'review';
type ItemCategory = 'discussion' | 'vote' | 'report' | 'break' | 'ceremony';
type MotionResult = 'passed' | 'failed' | 'tabled' | '';

interface MotionRecord {
  movedBy: string;
  secondedBy: string;
  result: MotionResult;
}

interface AgendaItem {
  id: string;
  topic: string;
  presenter: string;
  durationMinutes: number;
  category: ItemCategory;
  description: string;
  notes: string;
  motion?: MotionRecord;
  actualDurationSeconds?: number;
}

interface MeetingState {
  orgName: string;
  meetingTitle: string;
  date: string;
  startTime: string;
  targetDurationMinutes: number;
  location: string;
  headerNotes: string;
  items: AgendaItem[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'orgsets-meeting-agenda-builder';

const CATEGORY_CONFIG: Record<ItemCategory, { label: string; color: string; bgLight: string; bgDark: string }> = {
  discussion: { label: 'Discussion', color: 'text-[#0044FF]', bgLight: 'bg-blue-50', bgDark: 'dark:bg-blue-950/30' },
  vote:       { label: 'Vote',       color: 'text-red-600',    bgLight: 'bg-red-50',  bgDark: 'dark:bg-red-950/30' },
  report:     { label: 'Report',     color: 'text-emerald-600',bgLight: 'bg-emerald-50', bgDark: 'dark:bg-emerald-950/30' },
  break:      { label: 'Break',      color: 'text-amber-600',  bgLight: 'bg-amber-50', bgDark: 'dark:bg-amber-950/30' },
  ceremony:   { label: 'Ceremony',   color: 'text-purple-600', bgLight: 'bg-purple-50', bgDark: 'dark:bg-purple-950/30' },
};

const TEMPLATES: { name: string; icon: React.ReactNode; items: Omit<AgendaItem, 'id' | 'notes' | 'actualDurationSeconds'>[] }[] = [
  {
    name: "Robert's Rules Standard",
    icon: <Gavel className="w-5 h-5" />,
    items: [
      { topic: 'Call to Order & Roll Call', presenter: 'Chairperson', durationMinutes: 5, category: 'ceremony', description: '' },
      { topic: 'Approval of Previous Minutes', presenter: 'Secretary', durationMinutes: 5, category: 'vote', description: 'Review and approve minutes from the last meeting.', motion: { movedBy: '', secondedBy: '', result: '' } },
      { topic: "Officer Reports", presenter: 'Officers', durationMinutes: 15, category: 'report', description: '' },
      { topic: 'Committee Reports', presenter: 'Committee Chairs', durationMinutes: 20, category: 'report', description: '' },
      { topic: 'Unfinished Business', presenter: 'Chairperson', durationMinutes: 15, category: 'discussion', description: '' },
      { topic: 'New Business', presenter: 'Chairperson', durationMinutes: 20, category: 'discussion', description: '' },
      { topic: 'Open Forum / Public Comments', presenter: 'Members', durationMinutes: 10, category: 'discussion', description: '' },
      { topic: 'Adjournment', presenter: 'Chairperson', durationMinutes: 2, category: 'ceremony', description: '', motion: { movedBy: '', secondedBy: '', result: '' } },
    ],
  },
  {
    name: 'HOA Annual Meeting',
    icon: <Building2 className="w-5 h-5" />,
    items: [
      { topic: 'Call to Order & Quorum Verification', presenter: 'President', durationMinutes: 5, category: 'ceremony', description: '' },
      { topic: 'Approval of Previous Minutes', presenter: 'Secretary', durationMinutes: 5, category: 'vote', description: '', motion: { movedBy: '', secondedBy: '', result: '' } },
      { topic: "Treasurer's Financial Report", presenter: 'Treasurer', durationMinutes: 15, category: 'report', description: 'Review annual financial statements and reserve fund status.' },
      { topic: 'Annual Budget Approval', presenter: 'Treasurer', durationMinutes: 15, category: 'vote', description: '', motion: { movedBy: '', secondedBy: '', result: '' } },
      { topic: 'Maintenance & Capital Improvement Updates', presenter: 'Property Manager', durationMinutes: 15, category: 'report', description: '' },
      { topic: 'Election of Board Members', presenter: 'Secretary', durationMinutes: 20, category: 'vote', description: '', motion: { movedBy: '', secondedBy: '', result: '' } },
      { topic: 'Homeowner Open Forum', presenter: 'Members', durationMinutes: 15, category: 'discussion', description: '' },
      { topic: 'Adjournment', presenter: 'President', durationMinutes: 2, category: 'ceremony', description: '' },
    ],
  },
  {
    name: 'Nonprofit Board Meeting',
    icon: <Landmark className="w-5 h-5" />,
    items: [
      { topic: 'Call to Order', presenter: 'Board Chair', durationMinutes: 3, category: 'ceremony', description: '' },
      { topic: 'Mission Moment', presenter: 'Executive Director', durationMinutes: 5, category: 'ceremony', description: 'Share a story or impact metric that connects the board to the mission.' },
      { topic: 'Consent Agenda (Minutes, Reports)', presenter: 'Board Chair', durationMinutes: 5, category: 'vote', description: 'Approve routine items in a single motion.', motion: { movedBy: '', secondedBy: '', result: '' } },
      { topic: 'Financial Report & Dashboard', presenter: 'Treasurer', durationMinutes: 15, category: 'report', description: '' },
      { topic: 'Executive Director Report', presenter: 'Executive Director', durationMinutes: 15, category: 'report', description: '' },
      { topic: 'Strategic Discussion Item', presenter: 'Board Chair', durationMinutes: 20, category: 'discussion', description: '' },
      { topic: 'Fundraising Update', presenter: 'Development Chair', durationMinutes: 10, category: 'report', description: '' },
      { topic: 'Action Items & Next Steps', presenter: 'Board Chair', durationMinutes: 5, category: 'discussion', description: '' },
      { topic: 'Adjournment', presenter: 'Board Chair', durationMinutes: 2, category: 'ceremony', description: '' },
    ],
  },
  {
    name: 'Committee Report Meeting',
    icon: <Users className="w-5 h-5" />,
    items: [
      { topic: 'Roll Call & Attendance', presenter: 'Chair', durationMinutes: 3, category: 'ceremony', description: '' },
      { topic: 'Review of Assigned Action Items', presenter: 'Chair', durationMinutes: 10, category: 'report', description: 'Status update on tasks assigned in the previous meeting.' },
      { topic: 'Progress Report: Current Initiatives', presenter: 'Committee Members', durationMinutes: 20, category: 'report', description: '' },
      { topic: 'Discussion: Upcoming Milestones', presenter: 'Chair', durationMinutes: 15, category: 'discussion', description: '' },
      { topic: 'Budget & Resource Needs', presenter: 'Chair', durationMinutes: 10, category: 'discussion', description: '' },
      { topic: 'Recommendations to Full Board', presenter: 'Chair', durationMinutes: 10, category: 'vote', description: '', motion: { movedBy: '', secondedBy: '', result: '' } },
      { topic: 'Next Meeting Date & Adjournment', presenter: 'Chair', durationMinutes: 2, category: 'ceremony', description: '' },
    ],
  },
];

const DEFAULT_STATE: MeetingState = {
  orgName: 'My Board of Directors',
  meetingTitle: 'Regular Monthly Meeting',
  date: '',
  startTime: '18:00',
  targetDurationMinutes: 120,
  location: 'Community Center, Room A',
  headerNotes: "Please review the previous month's minutes prior to the meeting.",
  items: TEMPLATES[0].items.map((t, i) => ({
    ...t,
    id: String(i + 1),
    notes: '',
  })),
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
}

function formatDuration(totalSeconds: number): string {
  const m = Math.floor(Math.abs(totalSeconds) / 60);
  const s = Math.abs(totalSeconds) % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatTime12h(time24: string): string {
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr;
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h > 12) h -= 12;
  if (h === 0) h = 12;
  return `${h}:${m} ${ampm}`;
}

function calculateStartTimes(startTime: string, items: AgendaItem[]): string[] {
  const [hStr, mStr] = startTime.split(':');
  let totalMinutes = parseInt(hStr, 10) * 60 + parseInt(mStr, 10);
  return items.map(item => {
    const h = Math.floor(totalMinutes / 60) % 24;
    const m = totalMinutes % 60;
    const t = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    totalMinutes += item.durationMinutes;
    return formatTime12h(t);
  });
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateLong(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

// ─── Audio Chime ──────────────────────────────────────────────────────────────

function playChime() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.8);
    // Second tone
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.frequency.value = 1320;
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0, ctx.currentTime + 0.15);
    gain2.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1.0);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 1.0);
  } catch {
    // Audio not available
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function BoardMeetingAgendaBuilder() {
  // Load from localStorage or use defaults
  const [state, setState] = useState<MeetingState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as MeetingState;
        // Validate shape
        if (parsed.items && Array.isArray(parsed.items) && parsed.orgName) {
          return { ...DEFAULT_STATE, ...parsed };
        }
      }
    } catch { /* ignore */ }
    return { ...DEFAULT_STATE, date: todayISO() };
  });

  const [mode, setMode] = useState<MeetingMode>('build');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [meetingElapsedSeconds, setMeetingElapsedSeconds] = useState(0);
  const [itemElapsedSeconds, setItemElapsedSeconds] = useState(0);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showTemplates, setShowTemplates] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [buildNudgeDismissed, setBuildNudgeDismissed] = useState(false);
  const [reviewNudgeDismissed, setReviewNudgeDismissed] = useState(false);

  const navigate = useNavigate();

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chimePlayedRef = useRef<Set<number>>(new Set());

  // Persist to localStorage on state change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* quota exceeded, ignore */ }
  }, [state]);

  // Derived values
  const totalPlannedMinutes = state.items.reduce((s, i) => s + i.durationMinutes, 0);
  const isOverTarget = totalPlannedMinutes > state.targetDurationMinutes;
  const startTimes = calculateStartTimes(state.startTime, state.items);

  // ─── State Mutators ───────────────────────────────────────────────────────

  const updateField = useCallback(<K extends keyof MeetingState>(key: K, value: MeetingState[K]) => {
    setState(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateItem = useCallback((id: string, field: keyof AgendaItem, value: any) => {
    setState(prev => ({
      ...prev,
      items: prev.items.map(item => item.id === id ? { ...item, [field]: value } : item),
    }));
  }, []);

  const updateMotion = useCallback((id: string, field: keyof MotionRecord, value: string) => {
    setState(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id
          ? { ...item, motion: { ...(item.motion || { movedBy: '', secondedBy: '', result: '' }), [field]: value } }
          : item
      ),
    }));
  }, []);

  const addItem = useCallback(() => {
    const newItem: AgendaItem = {
      id: generateId(),
      topic: '',
      presenter: '',
      durationMinutes: 5,
      category: 'discussion',
      description: '',
      notes: '',
    };
    setState(prev => ({ ...prev, items: [...prev.items, newItem] }));
  }, []);

  const removeItem = useCallback((id: string) => {
    setState(prev => ({ ...prev, items: prev.items.filter(i => i.id !== id) }));
  }, []);

  const moveItem = useCallback((index: number, direction: -1 | 1) => {
    setState(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.items.length) return prev;
      const newItems = [...prev.items];
      [newItems[index], newItems[target]] = [newItems[target], newItems[index]];
      return { ...prev, items: newItems };
    });
  }, []);

  const applyTemplate = useCallback((templateIndex: number) => {
    const template = TEMPLATES[templateIndex];
    setState(prev => ({
      ...prev,
      items: template.items.map(t => ({ ...t, id: generateId(), notes: '' })),
    }));
    setShowTemplates(false);
  }, []);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ─── Timer Logic (PRESENT mode) ──────────────────────────────────────────

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setMeetingElapsedSeconds(prev => prev + 1);
      setItemElapsedSeconds(prev => prev + 1);
    }, 1000);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleStartMeeting = useCallback(() => {
    // Record actual durations for any items already elapsed
    setMode('present');
    setActiveItemIndex(0);
    setMeetingElapsedSeconds(0);
    setItemElapsedSeconds(0);
    setIsPaused(false);
    chimePlayedRef.current = new Set();
    // Reset actual durations
    setState(prev => ({
      ...prev,
      items: prev.items.map(i => ({ ...i, actualDurationSeconds: undefined })),
    }));
    startTimer();
  }, [startTimer]);

  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      startTimer();
    } else {
      stopTimer();
    }
    setIsPaused(prev => !prev);
  }, [isPaused, startTimer, stopTimer]);

  const handleNextItem = useCallback(() => {
    // Save actual duration for current item
    setState(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === activeItemIndex ? { ...item, actualDurationSeconds: itemElapsedSeconds } : item
      ),
    }));
    setItemElapsedSeconds(0);
    chimePlayedRef.current = new Set();
    setActiveItemIndex(prev => Math.min(prev + 1, state.items.length - 1));
  }, [activeItemIndex, itemElapsedSeconds, state.items.length]);

  const handlePrevItem = useCallback(() => {
    setState(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === activeItemIndex ? { ...item, actualDurationSeconds: itemElapsedSeconds } : item
      ),
    }));
    setItemElapsedSeconds(0);
    chimePlayedRef.current = new Set();
    setActiveItemIndex(prev => Math.max(prev - 1, 0));
  }, [activeItemIndex, itemElapsedSeconds]);

  const handleEndMeeting = useCallback(() => {
    stopTimer();
    // Save final item duration
    setState(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === activeItemIndex ? { ...item, actualDurationSeconds: itemElapsedSeconds } : item
      ),
    }));
    setMode('review');
  }, [stopTimer, activeItemIndex, itemElapsedSeconds]);

  // Chime effect — fires when an item goes over its allocated time
  useEffect(() => {
    if (mode !== 'present' || isPaused || isMuted) return;
    const currentItem = state.items[activeItemIndex];
    if (!currentItem) return;
    const allocatedSeconds = currentItem.durationMinutes * 60;
    if (itemElapsedSeconds === allocatedSeconds && !chimePlayedRef.current.has(activeItemIndex)) {
      chimePlayedRef.current.add(activeItemIndex);
      playChime();
    }
  }, [itemElapsedSeconds, activeItemIndex, mode, isPaused, isMuted, state.items]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // ─── Copy to Clipboard (REVIEW mode) ─────────────────────────────────────

  const handleCopyMinutes = useCallback(() => {
    const lines: string[] = [
      `${state.orgName}`,
      `${state.meetingTitle}`,
      `Date: ${formatDateLong(state.date)}`,
      `Location: ${state.location}`,
      '',
      '--- MEETING MINUTES ---',
      '',
    ];
    state.items.forEach((item, i) => {
      const actual = item.actualDurationSeconds != null
        ? `${Math.round(item.actualDurationSeconds / 60)} min`
        : `${item.durationMinutes} min (planned)`;
      lines.push(`${i + 1}. ${item.topic} [${item.presenter}] — ${actual}`);
      if (item.notes) lines.push(`   Notes: ${item.notes}`);
      if (item.motion && item.motion.result) {
        lines.push(`   Motion: ${item.motion.movedBy} / Seconded: ${item.motion.secondedBy} / Result: ${item.motion.result.toUpperCase()}`);
      }
      lines.push('');
    });
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  }, [state]);

  const handleReset = useCallback(() => {
    setState({ ...DEFAULT_STATE, date: todayISO() });
    setMode('build');
    setActiveItemIndex(0);
    setMeetingElapsedSeconds(0);
    setItemElapsedSeconds(0);
    chimePlayedRef.current = new Set();
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="text-left font-sans">
      {/* SCREEN UI */}
      <div className="print:hidden space-y-8">
        {/* ── Header Banner ─────────────────────────────────────────────── */}
        <div className="border-[8px] border-black dark:border-white rounded-none p-6 md:p-8 bg-[#FFD600] text-black relative shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] border-l-[24px] border-l-[#0044FF] transition-all">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl md:text-4xl font-black flex items-center mb-2 font-display uppercase tracking-tighter leading-none text-black">
                <FileSignature className="w-7 h-7 md:w-8 md:h-8 mr-3 text-black stroke-[3]" />
                Board Meeting Agenda Builder
              </h2>
              <p className="text-black font-bold max-w-xl text-sm md:text-base">
                Build your agenda, run the meeting with a live timer, and capture minutes — all in one tool.
              </p>
            </div>
            <div className="hidden md:block bg-black text-white p-4 border-[4px] border-black shadow-[4px_4px_0px_#0044FF] -rotate-2">
              <Timer className="w-12 h-12" />
            </div>
          </div>

          {/* Mode Tabs */}
          <div className="flex gap-0 mt-6 border-t-4 border-black pt-4">
            {([
              { key: 'build' as const, label: 'Build Agenda', icon: <LayoutTemplate className="w-4 h-4" /> },
              { key: 'present' as const, label: 'Live Meeting', icon: <Play className="w-4 h-4" /> },
              { key: 'review' as const, label: 'Review & Export', icon: <BarChart3 className="w-4 h-4" /> },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  if (tab.key === 'present' && mode !== 'present') {
                    handleStartMeeting();
                  } else {
                    if (mode === 'present' && tab.key !== 'present') {
                      stopTimer();
                    }
                    setMode(tab.key);
                  }
                }}
                className={cn(
                  'flex items-center gap-2 px-4 md:px-6 py-3 font-black uppercase tracking-wider text-xs md:text-sm border-2 border-black transition-all cursor-pointer',
                  mode === tab.key
                    ? 'bg-black text-white shadow-[4px_4px_0px_#0044FF]'
                    : 'bg-white text-black hover:bg-black hover:text-white'
                )}
              >
                {tab.icon}
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── BUILD MODE ────────────────────────────────────────────────── */}
        {mode === 'build' && (
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Controls Column */}
            <div className="lg:col-span-5 space-y-6">
              <Card className="rounded-none border-[8px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] p-6 bg-white dark:bg-black text-black dark:text-white transition-all space-y-6">
                <h3 className="text-xl font-black text-black dark:text-white uppercase tracking-tight mb-4 border-b-4 border-black dark:border-white pb-2 font-display">Meeting Details</h3>

                <div className="space-y-4">
                  <FieldLabel label="Organization Name">
                    <BauhausInput value={state.orgName} onChange={v => updateField('orgName', v)} />
                  </FieldLabel>
                  <FieldLabel label="Meeting Title">
                    <BauhausInput value={state.meetingTitle} onChange={v => updateField('meetingTitle', v)} />
                  </FieldLabel>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldLabel label="Date">
                      <input
                        type="date"
                        value={state.date}
                        onChange={e => updateField('date', e.target.value)}
                        className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-[#0044FF]"
                      />
                    </FieldLabel>
                    <FieldLabel label="Start Time">
                      <input
                        type="time"
                        value={state.startTime}
                        onChange={e => updateField('startTime', e.target.value)}
                        className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-[#0044FF]"
                      />
                    </FieldLabel>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <FieldLabel label="Location / Virtual Link">
                      <BauhausInput value={state.location} onChange={v => updateField('location', v)} />
                    </FieldLabel>
                    <FieldLabel label="Target Length (Min)">
                      <input
                        type="number"
                        value={state.targetDurationMinutes}
                        onChange={e => updateField('targetDurationMinutes', Math.max(1, Number(e.target.value)))}
                        className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-[#0044FF]"
                      />
                    </FieldLabel>
                  </div>
                  <FieldLabel label="Preparation Notes">
                    <textarea
                      value={state.headerNotes}
                      onChange={e => updateField('headerNotes', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-[#0044FF] placeholder:text-gray-400"
                    />
                  </FieldLabel>
                </div>

                {/* Total Duration vs Target */}
                <div className={cn(
                  'p-4 border-4 rounded-none flex items-center gap-4 transition-colors',
                  isOverTarget
                    ? 'border-red-600 bg-red-50 dark:bg-red-950/20'
                    : 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950/20'
                )}>
                  <Clock className={cn('w-8 h-8 shrink-0', isOverTarget ? 'text-red-600' : 'text-emerald-600')} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white">Total Agenda</div>
                    <div className={cn('text-2xl font-black font-mono tabular-nums', isOverTarget ? 'text-red-600' : 'text-emerald-600')}>
                      {totalPlannedMinutes} <span className="text-sm font-bold">/ {state.targetDurationMinutes} min</span>
                    </div>
                    {isOverTarget && (
                      <div className="text-xs font-black text-red-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {totalPlannedMinutes - state.targetDurationMinutes} minutes over target
                      </div>
                    )}
                    {/* Progress bar */}
                    <div className="w-full h-2 bg-black/10 dark:bg-white/10 mt-2 rounded-none overflow-hidden">
                      <div
                        className={cn('h-full transition-all', isOverTarget ? 'bg-red-600' : 'bg-emerald-600')}
                        style={{ width: `${Math.min(100, (totalPlannedMinutes / state.targetDurationMinutes) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* Templates */}
                <div>
                  <button
                    onClick={() => setShowTemplates(!showTemplates)}
                    className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-[#0044FF] hover:text-black dark:hover:text-white transition-colors cursor-pointer"
                  >
                    <BookOpen className="w-4 h-4" />
                    {showTemplates ? 'Hide Templates' : 'Load a Template'}
                    <ChevronRight className={cn('w-4 h-4 transition-transform', showTemplates && 'rotate-90')} />
                  </button>
                  <AnimatePresence>
                    {showTemplates && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          {TEMPLATES.map((t, i) => (
                            <button
                              key={t.name}
                              onClick={() => applyTemplate(i)}
                              className="flex items-center gap-2 p-3 border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] hover:bg-[#FFD600] hover:text-black text-black dark:text-white font-bold text-xs uppercase tracking-wider rounded-none shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff] transition-all cursor-pointer text-left"
                            >
                              {t.icon}
                              <span className="leading-tight">{t.name}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Agenda Items Header */}
                <h3 className="text-xl font-black text-black dark:text-white uppercase tracking-tight mt-4 mb-2 border-b-4 border-black dark:border-white pb-2 font-display">Agenda Items</h3>

                {/* Item List */}
                <div className="space-y-4">
                  {state.items.map((item, index) => {
                    const catConfig = CATEGORY_CONFIG[item.category];
                    return (
                      <div key={item.id} className={cn(
                        'p-4 rounded-none border-2 border-black dark:border-white relative group shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff] transition-all',
                        catConfig.bgLight, catConfig.bgDark
                      )}>
                        {/* Reorder & Delete Controls */}
                        <div className="absolute top-2 right-2 flex items-center gap-1">
                          <button onClick={() => moveItem(index, -1)} disabled={index === 0} className="p-1 text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white disabled:opacity-20 transition-colors cursor-pointer">
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button onClick={() => moveItem(index, 1)} disabled={index === state.items.length - 1} className="p-1 text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white disabled:opacity-20 transition-colors cursor-pointer">
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button onClick={() => removeItem(item.id)} className="p-1 text-black/40 dark:text-white/40 hover:text-red-600 transition-colors cursor-pointer" aria-label="Remove item">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="pr-20 space-y-3">
                          {/* Category badge + time */}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={cn('text-[9px] font-black uppercase tracking-widest px-2 py-0.5 border border-black/20 dark:border-white/20', catConfig.color)}>
                              {catConfig.label}
                            </span>
                            <span className="text-[9px] font-mono font-bold text-black/50 dark:text-white/50">
                              Starts {startTimes[index]}
                            </span>
                          </div>

                          <div>
                            <label className="text-[9px] uppercase font-black text-black dark:text-white block mb-1">Topic {index + 1}</label>
                            <BauhausInput value={item.topic} onChange={v => updateItem(item.id, 'topic', v)} small />
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-1">
                              <label className="text-[9px] uppercase font-black text-black dark:text-white block mb-1">Presenter</label>
                              <BauhausInput value={item.presenter} onChange={v => updateItem(item.id, 'presenter', v)} small />
                            </div>
                            <div>
                              <label className="text-[9px] uppercase font-black text-black dark:text-white block mb-1">Duration</label>
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min={1}
                                  value={item.durationMinutes}
                                  onChange={e => updateItem(item.id, 'durationMinutes', Math.max(1, Number(e.target.value)))}
                                  className="w-full px-2 py-1 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-black h-8 text-center focus:outline-none focus:ring-2 focus:ring-[#0044FF]"
                                />
                                <span className="text-[9px] font-black uppercase text-black/50 dark:text-white/50 whitespace-nowrap">min</span>
                              </div>
                            </div>
                            <div>
                              <label className="text-[9px] uppercase font-black text-black dark:text-white block mb-1">Type</label>
                              <select
                                value={item.category}
                                onChange={e => {
                                  const cat = e.target.value as ItemCategory;
                                  updateItem(item.id, 'category', cat);
                                  if (cat === 'vote' && !item.motion) {
                                    updateItem(item.id, 'motion', { movedBy: '', secondedBy: '', result: '' });
                                  }
                                }}
                                className="w-full px-2 py-1 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold h-8 focus:outline-none focus:ring-2 focus:ring-[#0044FF] cursor-pointer"
                              >
                                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                                  <option key={key} value={key}>{cfg.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Expandable description */}
                          <button
                            onClick={() => toggleExpanded(item.id)}
                            className="text-[9px] font-black uppercase tracking-widest text-[#0044FF] hover:text-black dark:hover:text-white transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <StickyNote className="w-3 h-3" />
                            {expandedItems.has(item.id) ? 'Hide' : 'Add'} Description
                          </button>
                          {expandedItems.has(item.id) && (
                            <textarea
                              value={item.description}
                              onChange={e => updateItem(item.id, 'description', e.target.value)}
                              placeholder="Background context for this agenda item..."
                              rows={2}
                              className="w-full px-2 py-1.5 text-xs border-2 border-black/30 dark:border-white/30 bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] placeholder:text-black/30 dark:placeholder:text-white/30"
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Add Item Button */}
                  <button
                    onClick={addItem}
                    className="w-full rounded-none border-4 border-black dark:border-white border-dashed bg-white hover:bg-gray-100 dark:bg-black dark:hover:bg-[#0A0A0A] text-black dark:text-white font-black uppercase tracking-wider py-3 mt-4 transition-all shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff] active:translate-y-0.5 active:shadow-[2px_2px_0px_#000] cursor-pointer"
                  >
                    <Plus className="w-4 h-4 mr-2 inline-block" /> Add Agenda Item
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 pt-6 border-t-4 border-black dark:border-white space-y-3">
                  <button
                    onClick={handleStartMeeting}
                    className="w-full gap-2 bg-[#0044FF] text-white hover:bg-black dark:hover:bg-white dark:hover:text-black font-black uppercase tracking-widest h-14 rounded-none border-4 border-black dark:border-white shadow-[6px_6px_0px_#FFD600] transition-all transform hover:-translate-y-1 hover:-translate-x-1 active:translate-y-0 active:translate-x-0 hover:shadow-[10px_10px_0px_#FFD600] active:shadow-[2px_2px_0px_#FFD600] cursor-pointer flex items-center justify-center"
                  >
                    <Play size={18} />
                    Start Live Meeting
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="w-full gap-2 bg-black text-white hover:bg-[#0044FF] dark:bg-white dark:text-black dark:hover:bg-[#0044FF] dark:hover:text-white font-black uppercase tracking-widest h-12 rounded-none border-4 border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] transition-all cursor-pointer flex items-center justify-center"
                  >
                    <Printer size={16} />
                    Print Agenda
                  </button>
                </div>

                {/* BUILD mode conversion bridge */}
                <div className="mt-6 border-4 border-black dark:border-white bg-[#FFD600] text-black p-4 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff]">
                  <p className="text-xs font-bold leading-snug">
                    Send this agenda to your board via SMS + Email and store the signed minutes permanently.
                  </p>
                  <button
                    onClick={() => navigate('/sign-up')}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider bg-black text-white hover:bg-[#0044FF] px-4 py-2 transition-colors cursor-pointer"
                  >
                    Run This Meeting on OrgSets <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </Card>
            </div>

            {/* Live Preview */}
            <div className="lg:col-span-7">
              <div className="sticky top-24">
                <div className="bg-[#F5F5F0] dark:bg-[#0A0A0A] border-[8px] border-black dark:border-white rounded-none p-6 flex justify-start overflow-x-auto shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#ffffff] min-h-[850px] overflow-y-auto relative">
                  <div className="origin-top lg:scale-[0.75] xl:scale-85 2xl:scale-100 shadow-[8px_8px_0px_#000] bg-white border-4 border-black pointer-events-none select-none w-[816px] shrink-0 min-h-[1056px] text-black text-left">
                    <AgendaTemplate
                      orgName={state.orgName}
                      meetingTitle={state.meetingTitle}
                      date={formatDateLong(state.date)}
                      time={formatTime12h(state.startTime)}
                      location={state.location}
                      headerNotes={state.headerNotes}
                      items={state.items}
                      startTimes={startTimes}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── PRESENT MODE ──────────────────────────────────────────────── */}
        {mode === 'present' && (
          <div className="space-y-6">
            {/* Global Meeting Timer Bar */}
            <Card className="rounded-none border-[8px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] p-0 bg-black text-white overflow-hidden">
              <div className="flex flex-col md:flex-row items-stretch">
                {/* Meeting Clock */}
                <div className="flex-1 p-6 flex items-center gap-4 border-b-4 md:border-b-0 md:border-r-4 border-white/20">
                  <Clock className="w-8 h-8 text-[#FFD600] shrink-0" />
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Meeting Elapsed</div>
                    <div className="text-3xl md:text-4xl font-black font-mono tabular-nums text-[#FFD600]">
                      {formatDuration(meetingElapsedSeconds)}
                    </div>
                  </div>
                </div>

                {/* Target Remaining */}
                <div className="flex-1 p-6 flex items-center gap-4 border-b-4 md:border-b-0 md:border-r-4 border-white/20">
                  <Timer className="w-8 h-8 text-white/50 shrink-0" />
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Target Remaining</div>
                    <div className={cn(
                      'text-3xl md:text-4xl font-black font-mono tabular-nums',
                      (state.targetDurationMinutes * 60 - meetingElapsedSeconds) < 0 ? 'text-red-500' : 'text-white'
                    )}>
                      {(state.targetDurationMinutes * 60 - meetingElapsedSeconds) < 0 ? '-' : ''}{formatDuration(state.targetDurationMinutes * 60 - meetingElapsedSeconds)}
                    </div>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center gap-3 p-6">
                  <button onClick={handlePauseResume} className={cn('p-3 rounded-none border-2 border-white font-black transition-all cursor-pointer', isPaused ? 'bg-[#FFD600] text-black' : 'bg-white/10 text-white hover:bg-white/20')}>
                    {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
                  </button>
                  <button onClick={() => setIsMuted(!isMuted)} className="p-3 rounded-none border-2 border-white/30 text-white/50 hover:text-white hover:border-white transition-all cursor-pointer">
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <button onClick={handleEndMeeting} className="p-3 rounded-none border-2 border-red-500 bg-red-600 text-white hover:bg-red-700 transition-all cursor-pointer" title="End Meeting">
                    <Square className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Overall progress bar */}
              <div className="h-2 bg-white/10">
                <div
                  className="h-full bg-[#FFD600] transition-all"
                  style={{ width: `${Math.min(100, (meetingElapsedSeconds / (state.targetDurationMinutes * 60)) * 100)}%` }}
                />
              </div>
            </Card>

            {/* Item Navigation */}
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={handlePrevItem}
                disabled={activeItemIndex === 0}
                className="flex items-center gap-2 px-6 py-3 border-4 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white font-black uppercase tracking-wider rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] disabled:opacity-30 transition-all cursor-pointer hover:bg-[#F5F5F0] dark:hover:bg-[#0A0A0A]"
              >
                <SkipBack className="w-4 h-4" /> Previous
              </button>
              <div className="text-center">
                <div className="text-[10px] font-black uppercase tracking-widest text-black/50 dark:text-white/50">Item</div>
                <div className="text-2xl font-black font-mono text-black dark:text-white">{activeItemIndex + 1} / {state.items.length}</div>
              </div>
              <button
                onClick={activeItemIndex === state.items.length - 1 ? handleEndMeeting : handleNextItem}
                className={cn(
                  'flex items-center gap-2 px-6 py-3 border-4 border-black dark:border-white font-black uppercase tracking-wider rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] transition-all cursor-pointer',
                  activeItemIndex === state.items.length - 1
                    ? 'bg-red-600 text-white hover:bg-red-700 border-red-800'
                    : 'bg-[#0044FF] text-white hover:bg-blue-700'
                )}
              >
                {activeItemIndex === state.items.length - 1 ? 'End Meeting' : 'Next'} <SkipForward className="w-4 h-4" />
              </button>
            </div>

            {/* Active Item Spotlight */}
            {state.items[activeItemIndex] && (() => {
              const item = state.items[activeItemIndex];
              const allocated = item.durationMinutes * 60;
              const remaining = allocated - itemElapsedSeconds;
              const isOver = remaining < 0;
              const catConfig = CATEGORY_CONFIG[item.category];

              return (
                <Card className={cn(
                  'rounded-none border-[8px] shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] p-0 overflow-hidden transition-all',
                  isOver ? 'border-red-600' : 'border-black dark:border-white'
                )}>
                  {/* Item Timer Header */}
                  <div className={cn(
                    'p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-colors',
                    isOver ? 'bg-red-600 text-white' : 'bg-[#F5F5F0] dark:bg-[#0A0A0A] text-black dark:text-white'
                  )}>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={cn(
                          'text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2',
                          isOver
                            ? 'border-white/40 text-white'
                            : `border-black/20 dark:border-white/20 ${catConfig.color}`
                        )}>
                          {catConfig.label}
                        </span>
                        {isPaused && (
                          <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-[#FFD600] bg-[#FFD600] text-black animate-pulse">
                            Paused
                          </span>
                        )}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tight font-display">
                        {item.topic || 'Untitled Item'}
                      </h3>
                      <p className={cn('text-sm font-bold mt-1', isOver ? 'text-white/70' : 'text-black/50 dark:text-white/50')}>
                        {item.presenter}
                      </p>
                      {item.description && (
                        <p className={cn('text-sm mt-2 max-w-xl', isOver ? 'text-white/60' : 'text-black/40 dark:text-white/40')}>
                          {item.description}
                        </p>
                      )}
                    </div>

                    {/* Countdown */}
                    <div className="text-right shrink-0">
                      <div className={cn('text-[10px] font-black uppercase tracking-widest mb-1', isOver ? 'text-white/60' : 'text-black/40 dark:text-white/40')}>
                        {isOver ? 'Over By' : 'Time Remaining'}
                      </div>
                      <div className={cn(
                        'text-5xl md:text-6xl font-black font-mono tabular-nums leading-none',
                        isOver ? 'text-white' : remaining < 60 ? 'text-red-600' : 'text-black dark:text-white'
                      )}>
                        {isOver ? '+' : ''}{formatDuration(remaining)}
                      </div>
                      <div className={cn('text-xs font-bold mt-2', isOver ? 'text-white/50' : 'text-black/30 dark:text-white/30')}>
                        Allocated: {item.durationMinutes} min
                      </div>
                    </div>
                  </div>

                  {/* Item countdown progress bar */}
                  <div className="h-3 bg-black/10 dark:bg-white/10">
                    <div
                      className={cn('h-full transition-all', isOver ? 'bg-red-600' : remaining < 60 ? 'bg-amber-500' : 'bg-[#0044FF]')}
                      style={{ width: `${Math.min(100, (itemElapsedSeconds / allocated) * 100)}%` }}
                    />
                  </div>

                  {/* Notes & Motion Section */}
                  <div className="p-6 md:p-8 bg-white dark:bg-black space-y-6">
                    {/* Notes */}
                    <div>
                      <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4" /> Meeting Notes
                      </label>
                      <textarea
                        value={item.notes}
                        onChange={e => updateItem(item.id, 'notes', e.target.value)}
                        rows={4}
                        placeholder="Capture discussion points, decisions, and action items..."
                        className="w-full px-4 py-3 text-sm border-4 border-black/20 dark:border-white/20 bg-[#F5F5F0] dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-[#0044FF] placeholder:text-black/25 dark:placeholder:text-white/25 resize-y"
                      />
                    </div>

                    {/* Motion Tracker (vote items only) */}
                    {item.category === 'vote' && item.motion && (
                      <div className="border-4 border-black/20 dark:border-white/20 p-4 bg-[#F5F5F0] dark:bg-[#0A0A0A]">
                        <label className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white flex items-center gap-2 mb-3">
                          <Gavel className="w-4 h-4" /> Motion Tracker
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="text-[9px] font-black uppercase tracking-widest text-black/50 dark:text-white/50 block mb-1">Moved By</label>
                            <BauhausInput value={item.motion.movedBy} onChange={v => updateMotion(item.id, 'movedBy', v)} small placeholder="Name..." />
                          </div>
                          <div>
                            <label className="text-[9px] font-black uppercase tracking-widest text-black/50 dark:text-white/50 block mb-1">Seconded By</label>
                            <BauhausInput value={item.motion.secondedBy} onChange={v => updateMotion(item.id, 'secondedBy', v)} small placeholder="Name..." />
                          </div>
                          <div>
                            <label className="text-[9px] font-black uppercase tracking-widest text-black/50 dark:text-white/50 block mb-1">Result</label>
                            <div className="flex gap-1">
                              {([
                                { value: 'passed' as const, icon: <Check className="w-4 h-4" />, label: 'Pass', color: 'bg-emerald-600 text-white border-emerald-700' },
                                { value: 'failed' as const, icon: <X className="w-4 h-4" />, label: 'Fail', color: 'bg-red-600 text-white border-red-700' },
                                { value: 'tabled' as const, icon: <Minus className="w-4 h-4" />, label: 'Table', color: 'bg-amber-500 text-black border-amber-600' },
                              ]).map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => updateMotion(item.id, 'result', item.motion?.result === opt.value ? '' : opt.value)}
                                  className={cn(
                                    'flex-1 flex items-center justify-center gap-1 py-1.5 px-2 text-[9px] font-black uppercase tracking-wider border-2 rounded-none transition-all cursor-pointer',
                                    item.motion?.result === opt.value
                                      ? opt.color
                                      : 'border-black/20 dark:border-white/20 bg-white dark:bg-black text-black dark:text-white hover:bg-black/5 dark:hover:bg-white/5'
                                  )}
                                >
                                  {opt.icon} {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })()}

            {/* Agenda Timeline Rail */}
            <Card className="rounded-none border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff] p-4 bg-white dark:bg-black">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-black/50 dark:text-white/50 mb-3">Agenda Timeline</h4>
              <div className="space-y-1">
                {state.items.map((item, i) => {
                  const isDone = i < activeItemIndex;
                  const isActive = i === activeItemIndex;
                  const catConfig = CATEGORY_CONFIG[item.category];
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        // Save current item duration before switching
                        setState(prev => ({
                          ...prev,
                          items: prev.items.map((it, idx) =>
                            idx === activeItemIndex ? { ...it, actualDurationSeconds: itemElapsedSeconds } : it
                          ),
                        }));
                        setItemElapsedSeconds(0);
                        chimePlayedRef.current = new Set();
                        setActiveItemIndex(i);
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 text-left border-2 rounded-none transition-all cursor-pointer',
                        isActive
                          ? 'border-[#0044FF] bg-[#0044FF]/10 dark:bg-[#0044FF]/20'
                          : isDone
                            ? 'border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 opacity-60'
                            : 'border-transparent hover:border-black/20 dark:hover:border-white/20'
                      )}
                    >
                      <span className={cn('text-[9px] font-mono font-black w-5', catConfig.color)}>{i + 1}</span>
                      <span className={cn('text-xs font-bold flex-1 truncate', isActive ? 'text-[#0044FF]' : isDone ? 'text-black/40 dark:text-white/40 line-through' : 'text-black dark:text-white')}>
                        {item.topic || 'Untitled'}
                      </span>
                      <span className="text-[9px] font-mono font-bold text-black/30 dark:text-white/30">
                        {item.durationMinutes}m
                      </span>
                      {isDone && item.actualDurationSeconds != null && (
                        <span className={cn(
                          'text-[9px] font-mono font-black',
                          item.actualDurationSeconds > item.durationMinutes * 60 ? 'text-red-500' : 'text-emerald-500'
                        )}>
                          {Math.round(item.actualDurationSeconds / 60)}m
                        </span>
                      )}
                      {isDone && <Check className="w-3 h-3 text-emerald-500 shrink-0" />}
                      {isActive && <ChevronRight className="w-3 h-3 text-[#0044FF] shrink-0 animate-pulse" />}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* ── REVIEW MODE ───────────────────────────────────────────────── */}
        {mode === 'review' && (
          <div className="space-y-6">
            {/* Summary Header */}
            <Card className="rounded-none border-[8px] border-black dark:border-white shadow-[8px_8px_0px_#000000] dark:shadow-[8px_8px_0px_#ffffff] p-6 md:p-8 bg-white dark:bg-black">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                  <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tighter font-display text-black dark:text-white">Meeting Summary</h3>
                  <p className="text-sm font-bold text-black/50 dark:text-white/50 mt-1">
                    {state.orgName} — {state.meetingTitle} — {formatDateLong(state.date)}
                  </p>
                </div>
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={handleCopyMinutes}
                    className={cn(
                      'flex items-center gap-2 px-5 py-3 border-4 font-black uppercase tracking-wider text-xs rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] transition-all cursor-pointer',
                      copySuccess
                        ? 'border-emerald-600 bg-emerald-600 text-white'
                        : 'border-black dark:border-white bg-white dark:bg-black text-black dark:text-white hover:bg-[#F5F5F0] dark:hover:bg-[#0A0A0A]'
                    )}
                  >
                    {copySuccess ? <Check className="w-4 h-4" /> : <ClipboardCopy className="w-4 h-4" />}
                    {copySuccess ? 'Copied!' : 'Copy Minutes'}
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="flex items-center gap-2 px-5 py-3 border-4 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black font-black uppercase tracking-wider text-xs rounded-none shadow-[4px_4px_0px_#FFD600] hover:bg-[#0044FF] hover:shadow-[6px_6px_0px_#FFD600] transition-all cursor-pointer"
                  >
                    <Printer className="w-4 h-4" /> Print Minutes
                  </button>
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-5 py-3 border-4 border-black/30 dark:border-white/30 bg-white dark:bg-black text-black/50 dark:text-white/50 font-black uppercase tracking-wider text-xs rounded-none transition-all cursor-pointer hover:border-red-500 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" /> Clear & Reset
                  </button>
                </div>
              </div>

              {/* Elapsed summary */}
              {meetingElapsedSeconds > 0 && (
                <div className="mt-6 pt-6 border-t-4 border-black/10 dark:border-white/10 flex flex-wrap gap-8">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-black/40 dark:text-white/40">Total Duration</div>
                    <div className="text-3xl font-black font-mono tabular-nums text-black dark:text-white">{formatDuration(meetingElapsedSeconds)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-black/40 dark:text-white/40">Target Was</div>
                    <div className="text-3xl font-black font-mono tabular-nums text-black/30 dark:text-white/30">{state.targetDurationMinutes} min</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-black/40 dark:text-white/40">
                      {meetingElapsedSeconds > state.targetDurationMinutes * 60 ? 'Over By' : 'Under By'}
                    </div>
                    <div className={cn(
                      'text-3xl font-black font-mono tabular-nums',
                      meetingElapsedSeconds > state.targetDurationMinutes * 60 ? 'text-red-600' : 'text-emerald-600'
                    )}>
                      {formatDuration(Math.abs(meetingElapsedSeconds - state.targetDurationMinutes * 60))}
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* REVIEW mode conversion card */}
            {!reviewNudgeDismissed && (
              <div className="border-4 border-[#0044FF] bg-[#0044FF] rounded-none p-6 relative shadow-[6px_6px_0px_#FFD600]">
                <button
                  onClick={() => setReviewNudgeDismissed(true)}
                  className="absolute top-3 right-3 text-white/50 hover:text-white transition-colors cursor-pointer p-1"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="pr-8">
                    <h4 className="text-lg font-black uppercase tracking-tight text-white font-display">
                      Send this agenda to your board via SMS + Email and store the signed minutes permanently.
                    </h4>
                    <p className="text-sm font-bold text-white/70 mt-1">
                      Your board members receive instant SMS reminders, vote on motions in real-time, and archive signed minutes securely in OrgSets.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/sign-up')}
                    className="shrink-0 bg-[#FFD600] text-black border-2 border-black font-black uppercase tracking-wider px-6 py-3 text-xs hover:bg-white transition-colors shadow-[3px_3px_0px_#000] cursor-pointer whitespace-nowrap"
                  >
                    Run This Meeting on OrgSets
                  </button>
                </div>
              </div>
            )}

            {/* Item-by-Item Review */}
            <div className="space-y-4">
              {state.items.map((item, i) => {
                const catConfig = CATEGORY_CONFIG[item.category];
                const hadActual = item.actualDurationSeconds != null;
                const actualMin = hadActual ? Math.round((item.actualDurationSeconds ?? 0) / 60) : null;
                const wasOver = hadActual && (item.actualDurationSeconds ?? 0) > item.durationMinutes * 60;
                const hasContent = item.notes || (item.motion && item.motion.result);

                return (
                  <Card key={item.id} className={cn(
                    'rounded-none border-4 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#ffffff] p-0 overflow-hidden transition-all',
                    hasContent ? 'border-black dark:border-white' : 'border-black/30 dark:border-white/30'
                  )}>
                    <div className="flex items-stretch">
                      {/* Time column */}
                      <div className={cn(
                        'w-20 shrink-0 flex flex-col items-center justify-center border-r-4 p-3',
                        wasOver ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800' : 'bg-[#F5F5F0] dark:bg-[#0A0A0A] border-black/10 dark:border-white/10'
                      )}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-black/40 dark:text-white/40">
                          {hadActual ? 'Actual' : 'Plan'}
                        </span>
                        <span className={cn(
                          'text-lg font-black font-mono',
                          wasOver ? 'text-red-600' : 'text-black dark:text-white'
                        )}>
                          {hadActual ? `${actualMin}m` : `${item.durationMinutes}m`}
                        </span>
                        {hadActual && (
                          <span className="text-[8px] font-bold text-black/30 dark:text-white/30">{item.durationMinutes}m plan</span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn('text-[9px] font-black uppercase tracking-widest', catConfig.color)}>
                            {catConfig.label}
                          </span>
                          <span className="text-xs font-bold text-black/30 dark:text-white/30">— {item.presenter}</span>
                        </div>
                        <h4 className="text-base font-black text-black dark:text-white">{item.topic || 'Untitled'}</h4>
                        {item.notes && (
                          <div className="mt-2 text-sm text-black/70 dark:text-white/70 border-l-4 border-[#0044FF] pl-3 whitespace-pre-wrap">
                            {item.notes}
                          </div>
                        )}
                        {item.motion && item.motion.result && (
                          <div className="mt-2 flex items-center gap-3 text-xs font-bold">
                            <Gavel className="w-4 h-4 text-black/40 dark:text-white/40" />
                            <span>Motion: {item.motion.movedBy || '—'}</span>
                            <span>Second: {item.motion.secondedBy || '—'}</span>
                            <span className={cn(
                              'px-2 py-0.5 font-black uppercase text-[9px] tracking-wider border-2',
                              item.motion.result === 'passed' ? 'bg-emerald-600 text-white border-emerald-700' :
                              item.motion.result === 'failed' ? 'bg-red-600 text-white border-red-700' :
                              'bg-amber-500 text-black border-amber-600'
                            )}>
                              {item.motion.result}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Over/under bar */}
                    {hadActual && (
                      <div className="h-1.5 bg-black/5 dark:bg-white/5">
                        <div
                          className={cn('h-full', wasOver ? 'bg-red-500' : 'bg-emerald-500')}
                          style={{ width: `${Math.min(100, ((item.actualDurationSeconds ?? 0) / (item.durationMinutes * 60)) * 100)}%` }}
                        />
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Back to Build */}
            <div className="text-center pt-4">
              <button
                onClick={() => setMode('build')}
                className="text-xs font-black uppercase tracking-widest text-[#0044FF] hover:text-black dark:hover:text-white transition-colors cursor-pointer"
              >
                ← Back to Agenda Builder
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── PRINT LAYOUT ────────────────────────────────────────────────── */}
      <div className="hidden print:block print:bg-white print:m-0 print:p-0 z-[9999] text-black">
        <style type="text/css" media="print">
          {`
            @page { size: letter; margin: 0.5in; }
            html, body {
              color-scheme: light !important;
              background: white !important;
              color: black !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            #root > header, #root > footer { display: none !important; }
            ::-webkit-scrollbar { display: none; }
          `}
        </style>
        {mode === 'review' ? (
          <MinutesTemplate state={state} meetingElapsedSeconds={meetingElapsedSeconds} />
        ) : (
          <AgendaTemplate
            orgName={state.orgName}
            meetingTitle={state.meetingTitle}
            date={formatDateLong(state.date)}
            time={formatTime12h(state.startTime)}
            location={state.location}
            headerNotes={state.headerNotes}
            items={state.items}
            startTimes={startTimes}
          />
        )}
      </div>
    </div>
  );
}

// ─── Sub-Components ───────────────────────────────────────────────────────────

function FieldLabel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-black text-black dark:text-white block mb-1.5 tracking-widest">{label}</label>
      {children}
    </div>
  );
}

function BauhausInput({ value, onChange, small, placeholder }: { value: string; onChange: (v: string) => void; small?: boolean; placeholder?: string }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        'w-full px-3 text-sm border-2 border-black dark:border-white bg-white dark:bg-[#0A0A0A] text-black dark:text-white rounded-none font-bold focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-[#0044FF] placeholder:text-black/25 dark:placeholder:text-white/25',
        small ? 'py-1 h-8' : 'py-2'
      )}
    />
  );
}

// ─── Print: Agenda Template ───────────────────────────────────────────────────

function AgendaTemplate({ orgName, meetingTitle, date, time, location, headerNotes, items, startTimes }: {
  orgName: string; meetingTitle: string; date: string; time: string; location: string; headerNotes: string; items: AgendaItem[]; startTimes: string[];
}) {
  return (
    <div className="p-12 font-serif text-slate-900 max-w-4xl mx-auto bg-white">
      {/* Header */}
      <div className="border-b-4 border-slate-900 pb-6 mb-8 text-center">
        <h2 className="text-3xl font-black uppercase tracking-widest mb-2 font-sans">{orgName}</h2>
        <h3 className="text-xl italic text-slate-700">{meetingTitle}</h3>
      </div>

      {/* Meta Info */}
      <div className="grid grid-cols-2 gap-4 mb-8 text-[15px] leading-relaxed">
        <div>
          <p><strong className="font-bold uppercase tracking-wider text-xs text-slate-500 mr-2 font-sans">Date:</strong> {date}</p>
          <p><strong className="font-bold uppercase tracking-wider text-xs text-slate-500 mr-2 font-sans">Time:</strong> {time}</p>
        </div>
        <div className="text-right">
          <p><strong className="font-bold uppercase tracking-wider text-xs text-slate-500 mr-2 font-sans">Location:</strong> {location}</p>
        </div>
      </div>

      {headerNotes && (
        <div className="mb-10 bg-slate-50 p-4 border-l-4 border-slate-300 italic text-sm">
          <strong>Preparation:</strong> {headerNotes}
        </div>
      )}

      {/* Agenda Items */}
      <div className="mb-12">
        <h3 className="text-xl font-bold uppercase tracking-widest border-b-2 border-slate-200 pb-2 mb-6 font-sans">Order of Business</h3>
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b-2 border-slate-900 font-sans text-xs uppercase tracking-widest text-slate-500">
              <th className="py-3 px-2 w-16">Item</th>
              <th className="py-3 px-2">Topic</th>
              <th className="py-3 px-2 w-48">Presenter</th>
              <th className="py-3 px-2 w-24 text-right">Time</th>
              <th className="py-3 px-2 w-20 text-right">Duration</th>
            </tr>
          </thead>
          <tbody className="text-[15px]">
            {items.map((item, index) => (
              <tr key={item.id} className="border-b border-slate-200 align-top">
                <td className="py-4 px-2 font-bold text-slate-400 font-sans">{index + 1}.</td>
                <td className="py-4 px-2 font-bold text-slate-900 pr-8">
                  {item.topic || '---'}
                  {item.description && (
                    <div className="text-sm font-normal text-slate-500 mt-1 italic">{item.description}</div>
                  )}
                </td>
                <td className="py-4 px-2 text-slate-600">{item.presenter || '---'}</td>
                <td className="py-4 px-2 text-right whitespace-nowrap text-slate-500">{startTimes[index]}</td>
                <td className="py-4 px-2 text-right whitespace-nowrap text-slate-400 font-mono text-sm">{item.durationMinutes} min</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer / Notes */}
      <div className="mt-16 page-break-inside-avoid">
        <h3 className="text-sm font-bold uppercase tracking-widest border-b border-slate-300 pb-2 mb-4 font-sans text-slate-500">Action Items & Notes</h3>
        <div className="space-y-8">
          <div className="border-b border-slate-300 w-full h-4"></div>
          <div className="border-b border-slate-300 w-full h-4"></div>
          <div className="border-b border-slate-300 w-full h-4"></div>
        </div>
      </div>
    </div>
  );
}

// ─── Print: Minutes Template ──────────────────────────────────────────────────

function MinutesTemplate({ state, meetingElapsedSeconds }: { state: MeetingState; meetingElapsedSeconds: number }) {
  return (
    <div className="p-12 font-serif text-slate-900 max-w-4xl mx-auto bg-white">
      <div className="border-b-4 border-slate-900 pb-6 mb-8 text-center">
        <h2 className="text-3xl font-black uppercase tracking-widest mb-2 font-sans">{state.orgName}</h2>
        <h3 className="text-xl italic text-slate-700">Meeting Minutes — {state.meetingTitle}</h3>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8 text-[15px] leading-relaxed">
        <div>
          <p><strong className="font-bold uppercase tracking-wider text-xs text-slate-500 mr-2 font-sans">Date:</strong> {formatDateLong(state.date)}</p>
          <p><strong className="font-bold uppercase tracking-wider text-xs text-slate-500 mr-2 font-sans">Location:</strong> {state.location}</p>
        </div>
        <div className="text-right">
          {meetingElapsedSeconds > 0 && (
            <p><strong className="font-bold uppercase tracking-wider text-xs text-slate-500 mr-2 font-sans">Total Duration:</strong> {formatDuration(meetingElapsedSeconds)}</p>
          )}
        </div>
      </div>

      {state.items.map((item, i) => {
        const hasContent = item.notes || (item.motion && item.motion.result);
        return (
          <div key={item.id} className={cn('mb-6 pb-6', i < state.items.length - 1 && 'border-b border-slate-200')}>
            <div className="flex items-baseline gap-3 mb-2">
              <span className="text-slate-400 font-bold font-sans">{i + 1}.</span>
              <h4 className="text-lg font-bold text-slate-900">{item.topic}</h4>
              <span className="text-sm text-slate-500">({item.presenter})</span>
              {item.actualDurationSeconds != null && (
                <span className="text-sm font-mono text-slate-400 ml-auto">
                  {Math.round(item.actualDurationSeconds / 60)} min
                </span>
              )}
            </div>
            {item.notes && (
              <div className="ml-8 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {item.notes}
              </div>
            )}
            {item.motion && item.motion.result && (
              <div className="ml-8 mt-2 text-sm font-bold text-slate-600">
                Motion by {item.motion.movedBy || '—'}, seconded by {item.motion.secondedBy || '—'} — <span className="uppercase">{item.motion.result}</span>
              </div>
            )}
            {!hasContent && (
              <div className="ml-8 text-sm italic text-slate-400">No notes recorded.</div>
            )}
          </div>
        );
      })}

      <div className="mt-16 border-t-2 border-slate-900 pt-6 text-sm text-slate-500 text-center font-sans">
        Minutes prepared using OrgSets Board Meeting Agenda Builder
      </div>
    </div>
  );
}
